"use client";

import { useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Datepicker from "react-tailwindcss-datepicker";
import { auditorSchema } from "../Schema/auditorSchema";
import {
  useAuditorDetails,
  useCreateAuditor,
  useCreateDocument,
  useUpdateteAuditor,
} from "../custom-hooks/queries";
import { countries } from "../utils/constants";
import { getBlobSasToken, useUpdateAuditor } from "../api/api-methods";
import { BlockBlobClient } from "@azure/storage-blob";
import { v4 as uuidv4 } from "uuid";
import ComboBox from "../components/ComboBox";
import { useParams } from "react-router-dom";
import { updateAuditorSchema } from "../Schema/updateAuditorSchema";
import { ArrowUpTrayIcon } from "@heroicons/react/20/solid";
import Notification from "../components/Notification";
import AuditorDetailSkeleton from "../components/Skeletons/auditorDetailSkeleton";

export default function AuditorDetail() {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(updateAuditorSchema),
  });
  const { mutate } = useCreateAuditor();

  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState(""); // State to store the file name
  const { mutate: createDocumentMutation, data: createDocData } =
    useCreateDocument();
  const { id } = useParams();
  const { data: auditorDetails, isLoading, refetch } = useAuditorDetails(id);
  const { mutate: updateAuditor } = useUpdateteAuditor();
  const newGuid = uuidv4();
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("success");

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name); // Set the file name when a file is selected
    } else {
      setFileName(""); // Clear the file name if no file is selected
    }
  };
  const onSubmit = async (data) => {
    const file = data.cv;

    try {
      // Fetch the SAS token and other required data
      if (
        auditorDetails.cv_document_id !== null ||
        createDocData?.document_id !== null
      ) {
        const blobData = await getBlobSasToken("vqa", newGuid);
        const { storage_account_name, container_name, sas_token } = blobData;

        const blobUrl = `https://${storage_account_name}.blob.core.windows.net/${container_name}/${fileName}?${sas_token}`;

        const blobClient = new BlockBlobClient(blobUrl);

        // Upload the file
        await blobClient.uploadData(file);

        // Create metadata payload
        const documentMetadataPayload = {
          document_name: fileName,
          document_type: "CV",
          document_version: "1.0",
          document_url: blobUrl.split("?")[0], // Exclude SAS token in the URL
          provider_id: null,
          vqa_id: null,
          is_draft: false,
          rfi_id: null,
          capabilities: [],
          document_location: null,
          provider_name: null,
        };

        // Save metadata to your database
        createDocumentMutation(documentMetadataPayload);
      }

      const auditorData = {
        first_name: data?.firstName,
        last_name: data?.lastName,
        office_phone: data?.officePhone,
        email_address: data.emailAddress,
        country: data.country.name,
        city: data.city,
        state: data.state,
        alternate_email_address: data.secondEmailAddress,
        company_affiliation: data.companyAffiliation,
        mobile_phone: data.mobilePhone,
        training_status: data.training,
        training_completion_date: data.dateOfCompletion.startDate,
        cv_document_id: createDocData?.document_id
          ? createDocData?.document_id
          : auditorDetails.cv_document_id || null,
      };

      updateAuditor(
        { auditorId: id, auditorData },
        {
          onSuccess: () => {
            setShow(true);
            setMessage("Updated successfully");
            setStatus("success");
            refetch();
          },
        },
        {
          onError: () => {
            setShow(true);
            setMessage("Error Updating");
            setStatus("error");
          },
        }
      );
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("An error occurred during upload. Please try again.");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {isLoading ? (
        <AuditorDetailSkeleton />
      ) : (
        <>
          <h1 className="text-2xl font-bold text-center mb-6">Auditor Form</h1>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  {...register("firstName")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="First Name"
                  defaultValue={auditorDetails?.first_name}
                />
                <p className="text-red-600 text-xs">
                  {errors.firstName?.message}
                </p>
              </div>

              {/* Last Name */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  {...register("lastName")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Last Name"
                  defaultValue={auditorDetails?.last_name}
                />
                <p className="text-red-600 text-xs">
                  {errors.lastName?.message}
                </p>
              </div>

              {/* Email Address */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  {...register("emailAddress")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Email Address"
                  defaultValue={auditorDetails?.email_address}
                />
                <p className="text-red-600 text-xs">
                  {errors.emailAddress?.message}
                </p>
              </div>

              {/* 2nd Email Address */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  2nd Email Address (Alternate)
                </label>
                <input
                  {...register("secondEmailAddress")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Alternate Email Address"
                  defaultValue={auditorDetails?.alternate_email_address}
                />
                <p className="text-red-600 text-xs">
                  {errors.secondEmailAddress?.message}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Date of Completion
                </label>
                <Controller
                  name="dateOfCompletion"
                  control={control}
                  render={({ field }) => (
                    <Datepicker
                      {...field}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select Date"
                      useRange={false}
                      displayFormat="MMMM DD, YYYY"
                      asSingle
                      minDate={new Date()}
                      popperClassName="datepicker-dropdown w-[300px] lg:w-[400px] xl:w-[500px] p-2 border border-gray-300 rounded-lg shadow-lg bg-white grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto"
                      inputClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                    />
                  )}
                />
                <p className="text-red-600 text-xs">
                  {errors.dateOfCompletion?.message}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Company Affiliation
                </label>
                <input
                  {...register("companyAffiliation")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Company Affiliation"
                  value={auditorDetails?.company_affiliation}
                />
                <p className="text-red-600 text-xs">
                  {errors.companyAffiliation?.message}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Office Phone
                </label>
                <input
                  {...register("officePhone")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Office Phone"
                  defaultValue={auditorDetails?.office_phone}
                />
                <p className="text-red-600 text-xs">
                  {errors.officePhone?.message}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Mobile Phone{" "}
                </label>
                <input
                  {...register("mobilePhone")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Mobile Phone"
                  defaultValue={auditorDetails?.mobile_phone}
                />
                <p className="text-red-600 text-xs">
                  {errors.mobilePhone?.message}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Country
                </label>
                <Controller
                  name="country"
                  control={control}
                  defaultValue={auditorDetails?.country}
                  render={({ field }) => (
                    <ComboBox
                      {...field} // Binds the field props automatically
                      data={countries}
                      placeHolder="Select Country"
                      setSelected={(value) => field.onChange(value)} // Update the value via Controller
                      selected={field.value} // Use the value from the field
                      width={"w-[300px] lg:w-[400px] xl:w-[595px] "}
                      defaultValue={auditorDetails?.country}
                    />
                  )}
                />

                <p className="text-red-600 text-xs">
                  {errors.country?.message}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  {...register("city")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="City"
                  defaultValue={auditorDetails?.city}
                />
                <p className="text-red-600 text-xs">{errors.city?.message}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  State/Province
                </label>
                <input
                  {...register("state")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Email Address"
                  defaultValue={auditorDetails?.state}
                />
                <p className="text-red-600 text-xs">{errors.state?.message}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Training
                </label>
                <input
                  {...register("training")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Training"
                  defaultValue={auditorDetails?.state}
                />
                <p className="text-red-600 text-xs">
                  {errors.training?.message}
                </p>
              </div>
              {/* CV Upload */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  <div>Upload CV</div>
                </label>
                <button
                  type="button"
                  onClick={() => fileInputRef.current.click()} // Trigger the file input click
                  className="inline-flex items-center justify-center w-12 mt-1 rounded-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  <ArrowUpTrayIcon className="w-5 h-5 " />
                </button>
                <input
                  {...register("cv", {
                    validate: (value) =>
                      (value && value[0]?.type === "application/pdf") ||
                      "Only PDF files are allowed", // Custom validation
                  })}
                  type="file"
                  ref={(e) => {
                    fileInputRef.current = e; // Keep the `ref` for manual click trigger
                    register("cv").ref(e); // Connect to `react-hook-form`
                  }}
                  onChange={(e) => {
                    handleFileChange(e); // Handle file display
                  }}
                  accept="application/pdf" // Restrict file type
                  className="hidden"
                />

                <span className="text-sm text-gray-700 ml-3">
                  <span className="ml-3 text-sm text-gray-700">{fileName}</span>{" "}
                </span>
                <p className="text-red-600 text-xs">{errors.cv?.message}</p>
              </div>
            </div>{" "}
            {/* Date of Completion */}
            <div className="flex">
              <button
                type="submit"
                className="inline-flex justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Update
              </button>
            </div>
          </form>
        </>
      )}
      <Notification
        show={show}
        setShow={setShow}
        errorMessage={message}
        status={status}
      />
    </div>
  );
}
